import DOMPurify from "dompurify";

/**
 * Etiquetas de formato que puede traer el sumario de una competición (lo redacta IronIA): párrafos, títulos, listas,
 * enlaces y el logo del patrocinador. Todo lo demás se elimina.
 */
const AllowedTags = ["a", "b", "strong", "i", "em", "u", "br", "p", "span", "ul", "ol", "li", "h2", "h3", "h4", "img"];

/**
 * Atributos permitidos: los de los enlaces y los de las imágenes. Nada de manejadores de eventos (onerror, onload...).
 * DOMPurify ya descarta por defecto los href y src con "javascript:" y similares. "style" solo se conserva en las
 * imágenes y filtrado (ver StyleDeclarationAllowed).
 */
const AllowedAttributes = ["href", "target", "rel", "src", "alt", "width", "height", "style"];

/**
 * Declaraciones CSS que se conservan en el style de una imagen: las que colocan el logo a la izquierda del texto
 * (float: left; margin-right: 15px). Cualquier otra (position, background, url()...) se quita para que el sumario
 * no pueda tapar ni imitar otras partes de la página.
 */
const StyleDeclarationAllowed = /^(float|margin(-(top|right|bottom|left))?)\s*:\s*[a-z0-9.%\s-]+$/i;

/**
 * Instancia propia de DOMPurify, para que sus hooks no afecten a los demás saneados de la web (barra de estado...).
 */
const competitionPurifier = DOMPurify(window);

competitionPurifier.addHook("uponSanitizeAttribute", (node, data) =>
{
    if(data.attrName !== "style")
    {
        return;
    }

    if(node.nodeName !== "IMG")
    {
        data.keepAttr = false;
        return;
    }

    const declarations = data.attrValue
        .split(";")
        .map((declaration) => declaration.trim())
        .filter((declaration) => StyleDeclarationAllowed.test(declaration));

    if(declarations.length === 0)
    {
        data.keepAttr = false;
        return;
    }

    data.attrValue = declarations.join("; ") + ";";
});

competitionPurifier.addHook("afterSanitizeAttributes", (node) =>
{
    // Los enlaces que abren otra pestaña no deben poder controlar esta (window.opener)
    if(node.nodeName === "A" && node.hasAttribute("target"))
    {
        node.setAttribute("rel", "noopener noreferrer");
    }
});

/**
 * Sanea el sumario de una competición antes de pintarlo con dangerouslySetInnerHTML. Se guarda en base de datos y lo
 * ven todos los usuarios: si alguna vía deja pasar una etiqueta peligrosa, aquí se quita antes de que se ejecute en
 * su sesión (auditoría de seguridad G4-07).
 */
export function sanitizeCompetitionHtml(html?: string | null): string
{
    if(!html)
    {
        return "";
    }

    return competitionPurifier.sanitize(html, { ALLOWED_TAGS: AllowedTags, ALLOWED_ATTR: AllowedAttributes });
}

/**
 * Texto plano de la descripción o el sumario de una competición, recortado a un número de caracteres, para pintarlo
 * como texto en las tarjetas. La descripción la escribe el creador de la competición y la ven todos los usuarios, así
 * que no se pinta nunca como HTML (auditoría de seguridad G4-07). Se quitan todas las etiquetas, y el contenido de
 * script y style no se conserva.
 */
export function competitionHtmlToText(html: string | null | undefined, limit: number): string
{
    if(!html)
    {
        return "";
    }

    const fragment = DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [], KEEP_CONTENT: true, RETURN_DOM_FRAGMENT: true });
    const text = fragment.textContent ?? "";

    if(text.length > limit)
    {
        return text.substring(0, limit) + "...";
    }

    return text;
}
